import { useEffect, useMemo, useState } from 'react';
import { computeDayEvent, toDateStr, type CalendarEvent } from '@/lib/hebcal';
import { fetchUserProfile, profileGeo } from '@/services/profile';

function formatDateDisplay(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function getBannerStyle(event: CalendarEvent | null): {
  bgClass: string;
  borderClass: string;
  textClass: string;
  icon: string;
  label: string;
} {
  if (!event) {
    return {
      bgClass: 'bg-background-100',
      borderClass: 'border-background-200/60',
      textClass: 'text-foreground-700',
      icon: 'ri-calendar-line',
      label: '',
    };
  }
  if (event.isYomTov && event.holidayName) {
    return {
      bgClass: 'bg-rose-50/60',
      borderClass: 'border-rose-200/60',
      textClass: 'text-rose-800',
      icon: 'ri-candle-line',
      label: event.holidayName,
    };
  }
  if (event.isShabbat) {
    return {
      bgClass: 'bg-indigo-50/60',
      borderClass: 'border-indigo-200/60',
      textClass: 'text-indigo-800',
      icon: 'ri-star-line',
      label: event.holidayName || 'Shabbat',
    };
  }
  if (event.isRoshChodesh && event.holidayName) {
    return {
      bgClass: 'bg-teal-50/60',
      borderClass: 'border-teal-200/60',
      textClass: 'text-teal-800',
      icon: 'ri-moon-clear-line',
      label: event.holidayName,
    };
  }
  if (event.holidayName) {
    return {
      bgClass: 'bg-amber-50/60',
      borderClass: 'border-amber-200/60',
      textClass: 'text-amber-800',
      icon: 'ri-calendar-check-line',
      label: event.holidayName,
    };
  }
  return {
    bgClass: 'bg-background-100',
    borderClass: 'border-background-200/60',
    textClass: 'text-foreground-700',
    icon: 'ri-calendar-line',
    label: '',
  };
}

export default function HebrewDateBanner() {
  const todayKey = useMemo(() => toDateStr(new Date()), []);
  const [event, setEvent] = useState<CalendarEvent | null>(null);

  useEffect(() => {
    let mounted = true;
    fetchUserProfile().then((profile) => {
      if (!mounted) return;
      setEvent(computeDayEvent(new Date(), profileGeo(profile)));
    });
    return () => { mounted = false; };
  }, []);

  const style = getBannerStyle(event);

  if (!event) return null;

  return (
    <div className={`rounded-lg border ${style.bgClass} ${style.borderClass} px-4 py-3 flex items-center justify-between`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${style.bgClass} bg-white/60`}>
          <i className={`${style.icon} text-lg ${style.textClass}`} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-heading font-semibold ${style.textClass} whitespace-nowrap`}>
              {event.hebrewDate}
            </span>
            {event.parsha && (
              <span className="text-xs text-foreground-500 bg-background-200/60 px-2 py-0.5 rounded-full whitespace-nowrap">
                Parshat {event.parsha}
              </span>
            )}
          </div>
          <p className="text-xs text-foreground-500 mt-0.5">
            {formatDateDisplay(todayKey)}
            {event.candleLighting && (
              <span className="ml-2 text-amber-600 whitespace-nowrap">
                🕯️ Candle Lighting: {event.candleLighting}
              </span>
            )}
            {event.fastBegins && (
              <span className="ml-2 text-accent-600 whitespace-nowrap">
                Fast begins: {event.fastBegins}
              </span>
            )}
            {event.fastEnds && (
              <span className="ml-2 text-accent-600 whitespace-nowrap">
                Fast ends: {event.fastEnds}
              </span>
            )}
            {event.havdalah && (
              <span className="ml-2 text-indigo-500 whitespace-nowrap">
                Havdalah: {event.havdalah}
              </span>
            )}
          </p>
        </div>
      </div>

      {style.label && (
        <span className={`text-xs font-semibold px-3 py-1 rounded-full flex-shrink-0 ml-3 ${style.bgClass} bg-white/60 ${style.textClass} whitespace-nowrap`}>
          {style.label}
        </span>
      )}

      {event.isShabbat && !event.isYomTov && (
        <span className="text-xs font-semibold px-3 py-1 rounded-full flex-shrink-0 ml-3 bg-indigo-100/80 text-indigo-700 whitespace-nowrap">
          Shabbat Shalom
        </span>
      )}

      {event.isYomTov && (
        <span className="text-xs font-semibold px-3 py-1 rounded-full flex-shrink-0 ml-3 bg-rose-100/80 text-rose-700 whitespace-nowrap">
          Chag Sameach
        </span>
      )}
    </div>
  );
}